import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { publicApi } from '../api';
import { Truck, Search, Package, Clock, CheckCircle2, XCircle } from 'lucide-react';

const STATUS_META = {
  pending:    { label: 'Order Placed',  color: '#F4A300', icon: Clock },
  confirmed:  { label: 'Confirmed',     color: '#2A9D8F', icon: CheckCircle2 },
  dispatched: { label: 'Dispatched',    color: '#4A90E2', icon: Truck },
  delivered:  { label: 'Delivered',     color: '#2A9D8F', icon: Package },
  cancelled:  { label: 'Cancelled',     color: '#E63946', icon: XCircle },
};

const TrackOrder = () => {
  const [params] = useSearchParams();
  const [form, setForm] = useState({ id: params.get('id') || '', email: params.get('email') || '' });
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(''); setOrder(null);
    try {
      const res = await publicApi.trackOrder(form.id.trim(), form.email.trim());
      setOrder(res);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Order not found. Check your ID and email.');
    } finally {
      setLoading(false);
    }
  };

  const input = 'flex-1 py-3 bg-transparent focus:outline-none font-brico';

  return (
    <div className="bg-[#FFF4E6] min-h-screen">
      <Navbar />
      <section className="px-5 md:px-8 pt-28 pb-16">
        <div className="max-w-2xl mx-auto">
          <div className="text-xs font-bold uppercase tracking-widest text-[#E63946]">Track order</div>
          <h1 className="font-display text-5xl md:text-7xl uppercase leading-[0.9] mt-2">Where's my crunch?</h1>
          <p className="font-brico text-lg mt-3 opacity-70">Enter your Order ID and the email you used to place it.</p>

          <form onSubmit={submit} className="mt-6 bg-white rounded-3xl crunch-shadow p-6 md:p-8 space-y-3">
            <div className="flex items-center gap-2 bg-[#FFF4E6] rounded-2xl border-2 border-[#1a1a1a] px-4">
              <input required value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} placeholder="Order ID" className={input} />
            </div>
            <div className="flex items-center gap-2 bg-[#FFF4E6] rounded-2xl border-2 border-[#1a1a1a] px-4">
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className={input} />
            </div>
            <button disabled={loading} className="btn-chunky primary w-full justify-center">
              <Search size={16} /> {loading ? 'Searching…' : 'Track Order'}
            </button>
            {error && <div className="text-sm text-[#E63946] font-brico font-semibold">{error}</div>}
          </form>

          {order && (
            <OrderCard order={order} />
          )}
        </div>
      </section>
      <Footer />
    </div>
  );
};

const OrderCard = ({ order }) => {
  const meta = STATUS_META[order.status] || STATUS_META.pending;
  const Icon = meta.icon;
  const steps = ['pending', 'confirmed', 'dispatched', 'delivered'];
  const idx = steps.indexOf(order.status);
  const isCancelled = order.status === 'cancelled';

  return (
    <div className="mt-6 bg-white rounded-3xl crunch-shadow p-6 md:p-8">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest opacity-70">Order ID</div>
          <div className="font-display text-lg break-all">{order.id}</div>
        </div>
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-white font-brico font-bold text-xs uppercase tracking-widest" style={{ background: meta.color }}>
          <Icon size={14} /> {meta.label}
        </div>
      </div>

      {/* Progress bar */}
      {!isCancelled && (
        <div className="mt-6">
          <div className="flex items-center justify-between">
            {steps.map((s, i) => {
              const done = i <= idx;
              const M = STATUS_META[s];
              const StepIcon = M.icon;
              return (
                <div key={s} className="flex-1 flex flex-col items-center relative">
                  {i > 0 && <div className={`absolute right-1/2 top-4 h-1 w-full ${i <= idx ? 'bg-[#2A9D8F]' : 'bg-black/10'}`} />}
                  <div className={`relative z-10 w-8 h-8 rounded-full flex items-center justify-center text-white ${done ? '' : 'opacity-30'}`} style={{ background: done ? M.color : '#1a1a1a' }}>
                    <StepIcon size={14} />
                  </div>
                  <div className={`text-[10px] font-bold uppercase tracking-widest mt-2 text-center ${done ? '' : 'opacity-50'}`}>{M.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {order.tracking_id && (
        <div className="mt-6 bg-[#FFF4E6] rounded-2xl p-4 border-2 border-dashed border-[#1a1a1a]/20">
          <div className="text-xs font-bold uppercase tracking-widest opacity-70 flex items-center gap-2"><Truck size={12} /> Tracking</div>
          <div className="mt-1 font-display text-xl">{order.courier}</div>
          <div className="font-mono text-sm mt-1">{order.tracking_id}</div>
        </div>
      )}

      <div className="mt-6 pt-4 border-t-2 border-black/10">
        <div className="text-xs font-bold uppercase tracking-widest opacity-70">Items</div>
        <div className="mt-2 space-y-1">
          {order.items.map((i, idx2) => (
            <div key={idx2} className="flex items-center justify-between font-brico"><span>{i.title} ×{i.qty}</span><span className="font-bold">₹{i.price * i.qty}</span></div>
          ))}
        </div>
        <div className="mt-3 pt-3 border-t border-black/10 space-y-1">
          <div className="flex items-center justify-between font-brico"><span>Subtotal</span><span>₹{order.subtotal}</span></div>
          {order.discount > 0 && (
            <div className="flex items-center justify-between font-brico text-[#2A9D8F]"><span>Discount ({order.coupon_code})</span><span>−₹{order.discount}</span></div>
          )}
          <div className="flex items-center justify-between font-brico"><span>Shipping</span><span>{order.shipping === 0 ? 'FREE' : `₹${order.shipping}`}</span></div>
          <div className="flex items-center justify-between font-display text-2xl uppercase pt-2 border-t border-black/10"><span>Total</span><span>₹{order.total}</span></div>
        </div>
      </div>

      <Link to="/shop" className="btn-chunky outline mt-6 inline-flex">Shop More</Link>
    </div>
  );
};

export default TrackOrder;
